import { createContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AppRoutes from "./app/routes/AppRoutes";
import TimeTrackerProvider from "./components/organisms/TimeTrackerProvider/TimeTrackerProvider";
import SideNavbar from "./components/molecules/SideNavbar/SideNavbar";
import Header from "./components/organisms/Header/Header";
import Footer from "./components/organisms/Footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import {
  setClientSource,
  setColors,
  setTheme,
} from "./Redux/Slices/themeSlice";
import { ToastContainer } from "react-toastify";
import "./App.css";
import "./Global.css";
import { getClientSource } from "./app/utils/common/Functions";
import { setThemeColorsFromURL } from "./theme/themeHelper";
import { Modal, Input as AntInput } from "antd";
import { changePasswordAPI, ensureUserProfile } from "./Redux/Apis/authApi";
import { showErrorToast, showSuccessToast } from "./app/utils/common/Toast";

export const ThemeContext = createContext(null);

function App() {
  const location = useLocation();
  const notDashboardPaths = ["/not-found", "/login", "/"];
  const showHeaderFooter = !notDashboardPaths.includes(location.pathname);
  const isProjectBoardRoute = location.pathname.startsWith("/project-management");
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth.auth);
  const [isForcePasswordModalOpen, setIsForcePasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordSubmitLoading, setPasswordSubmitLoading] = useState(false);

  // const [theme, setTheme] = useState("light");

  useEffect(() => {
    const sourceTheme = {
      tcs: {
        title: "TCS Dashboard",
        favicon: "/icon/tcs-logo.png",
      },
      sattva: {
        title: "Sattva Dashboard",
        favicon: "/icon/sattva-logo.png",
      },
    };
    const { title, favicon } = sourceTheme[getClientSource()];
    document.title = title;
    document.querySelector('link[rel="icon"]').href = favicon;
    dispatch(setClientSource(getClientSource()));
    let colors = setThemeColorsFromURL(getClientSource());
    dispatch(setColors(colors));
  }, []);

  const toggleTheme = () => {
    dispatch(setTheme(theme === "light" ? "dark" : "light"));
  };
  const MOBILE_BREAKPOINT = 1024;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleViewportChange = (event) => {
      const mobile = event.matches;
      setIsMobile(mobile);
      setMobileMenuOpen(false);
      if (!mobile) {
        setSidebarCollapsed(false);
      }
    };

    handleViewportChange(mediaQuery);
    mediaQuery.addEventListener("change", handleViewportChange);
    return () => mediaQuery.removeEventListener("change", handleViewportChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileMenuOpen) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileMenuOpen]);

  const shouldForcePasswordChange = useMemo(
    () => Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true,
    [user?.mustChangePassword]
  );

  useEffect(() => {
    if (shouldForcePasswordChange) {
      setIsForcePasswordModalOpen(true);
      showErrorToast("Please change your password before continuing.");
    } else {
      setIsForcePasswordModalOpen(false);
    }
  }, [shouldForcePasswordChange]);

  const handleForcePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      showErrorToast("All password fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      showErrorToast("Confirm password must match new password.");
      return;
    }

    try {
      setPasswordSubmitLoading(true);
      await dispatch(
        changePasswordAPI({
          currentPassword,
          newPassword,
          confirmPassword,
        })
      ).unwrap();
      await ensureUserProfile(dispatch, { force: true });
      showSuccessToast("Password changed successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setIsForcePasswordModalOpen(false);
    } catch (_error) {
      // Error toast is handled by reducer.
    } finally {
      setPasswordSubmitLoading(false);
    }
  };

  const handleToggle = () => {
    if (isMobile) {
      setMobileMenuOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => !prev);
    }
  };

  const handleCloseMobileSidebar = () => {
    setMobileMenuOpen(false);
  };
  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <div
        className={`${showHeaderFooter ? "App" : ""}`}
        id={`${showHeaderFooter ? theme : ""}`}
      >
        {showHeaderFooter && user && (
          <SideNavbar
            collapsed={sidebarCollapsed}
            drawerOpen={mobileMenuOpen}
            isMobile={isMobile}
            onCloseMobile={handleCloseMobileSidebar}
          />
        )}
        <TimeTrackerProvider>
          <div className={`flex flex-col flex-1 min-w-0 min-h-0 ${showHeaderFooter ? "dashboard-shell" : ""} ${isProjectBoardRoute ? "dashboard-shell--project-board" : ""}`}>
            {showHeaderFooter && user && (
              <Header
                toggleTheme={toggleTheme}
                theme={theme}
                sidebarCollapsed={sidebarCollapsed}
                mobileMenuOpen={mobileMenuOpen}
                isMobile={isMobile}
                handleToggle={handleToggle}
              />
            )}
            <div
              className={`${showHeaderFooter
                ? `flex-1 min-h-0 shell-content-wrap${isProjectBoardRoute ? " shell-content-wrap--project-board" : ""}`
                : "flex justify-center items-center h-auto"
                }`}
            >
              <AppRoutes />
            </div>
            {showHeaderFooter && <Footer />}
          </div>
        </TimeTrackerProvider>
      </div>
      <ToastContainer />
      <Modal
        title="Change Password Required"
        open={isForcePasswordModalOpen}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText="Update Password"
        cancelButtonProps={{ style: { display: "none" } }}
        confirmLoading={passwordSubmitLoading}
        onOk={handleForcePasswordChange}
      >
        <p className="mb-3">For security, update your password now.</p>
        <div className="flex flex-col gap-3">
          <AntInput.Password
            placeholder="Current password"
            value={passwordForm.currentPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))
            }
            className="text-black"
          />
          <AntInput.Password
            placeholder="New password"
            value={passwordForm.newPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))
            }
          />
          <AntInput.Password
            placeholder="Confirm new password"
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
            }
          />
        </div>
      </Modal>
    </ThemeContext.Provider>
  );
}

export default App;
